/**
 * Spend checks on the model gateway page: the latest checks platform admins ran of a provider's
 * spend against the cost the gateway recorded, and the kinds of call well above their usual share
 * in the last 24 hours (services/spend-check.ts, plan 15 section 4.10).
 *
 * Read only: checks are run from the platform's AI costs page or over MCP, where who ran each is
 * known. Its own read, so a failure here says so and never takes the page down; the error is
 * checked before the empty state, because "no checks yet" and "could not be read" differ.
 *
 * @module router/web/pages/SpendCheckCard
 */

import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, RefreshCw, Scale } from 'lucide-react';
import { api, ApiError } from '../lib/api.js';
import { Badge, GlassButton, GlassCard } from '../ui/index.js';
import {
  figureWords, providerName, readSpendChecks, runBadge, runLine, spikeLine, usd, whenLabel,
  type CheckRun, type SpendChecksView,
} from './spend-check-view.js';

const HELP = 'A platform admin picks a provider and a period, and the provider\'s spend is compared with the cost the gateway recorded '
  + 'for it. A gap over 2%, or over $1, needs a look. Run checks from the platform\'s AI costs page, or over MCP.';

function RunRow({ run, open, onToggle }: { run: CheckRun; open: boolean; onToggle: () => void }) {
  const badge = runBadge(run);
  return (
    <>
      <tr className="border-b border-white/5">
        <td className="py-2 pr-3">
          <button type="button" onClick={onToggle} aria-expanded={open} className="inline-flex items-center gap-1 text-white/80 hover:text-white">
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {whenLabel(run.ranAt)}
          </button>
        </td>
        <td className="py-2 pr-3 text-white/80">{providerName(run.provider)}</td>
        <td className="py-2 pr-3 text-right tabular-nums text-white/80">{usd(run.providerUsd)}</td>
        <td className="py-2 pr-3 text-right tabular-nums text-white/80">{usd(run.recordedUsd)}</td>
        <td className="py-2 pr-3 text-right tabular-nums text-white/80">{usd(run.gapUsd)}</td>
        <td className="py-2"><Badge variant={badge.variant}>{badge.label}</Badge></td>
      </tr>
      {open && (
        <tr className="border-b border-white/5 bg-white/[0.02]">
          <td colSpan={6} className="px-3 py-3 text-xs text-white/70">
            <p className="text-white/80">{runLine(run)}</p>
            <p className="mt-1 text-white/55">
              {whenLabel(run.from)} to {whenLabel(run.to)}. The provider's figure was {figureWords(run.figureSource)}.
              Run by {run.ranBy.name ?? run.ranBy.id}.
            </p>
            {run.unmatched.length > 0 && (
              <div className="mt-2">
                <p className="text-white/60">
                  {run.estimatedCalls} {run.estimatedCalls === 1 ? 'call was' : 'calls were'} not at the provider's own figure. Look these up at the provider:
                </p>
                <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto font-mono text-[11px] text-white/70">
                  {run.unmatched.map((u) => (
                    <li key={u.requestId} className="break-all">{u.generationId ?? `no generation id (${u.requestId})`} · {usd(u.recordedUsd)} recorded</li>
                  ))}
                </ul>
                {run.unmatchedMore > 0 && <p className="mt-1 text-white/50">And {run.unmatchedMore} more.</p>}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function SpendCheckCard() {
  const [view, setView] = useState<SpendChecksView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setView(readSpendChecks(await api.get<unknown>('/api/v1/platform/spend-checks')));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'The spend checks could not be read.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <GlassCard className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-indigo-300" />
          <h3 className="text-sm font-semibold text-white" title={HELP}>Spend checks</h3>
          {view && view.spikes.length > 0 && <Badge variant="warning">Unusual in the last 24 hours</Badge>}
        </div>
        <GlassButton variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void load()} loading={loading}>
          Refresh
        </GlassButton>
      </div>
      <p className="text-xs text-white/50">{HELP}</p>

      {error ? (
        <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
          The spend checks could not be read. {error} This is not the same as nothing to report.
        </div>
      ) : !view && loading ? (
        <div className="flex items-center gap-2 text-xs text-white/50"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the checks…</div>
      ) : view ? (
        <>
          {view.spikes.map((s) => <p key={s.kind} className="text-xs text-amber-200/90">{spikeLine(s)}</p>)}
          {view.runs.length === 0 ? (
            <p className="text-xs text-white/60">No spend check has been run yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left text-xs text-white/50">
                    <th className="pb-2 pr-3 font-medium">Checked</th>
                    <th className="pb-2 pr-3 font-medium">Provider</th>
                    <th className="pb-2 pr-3 text-right font-medium">Billed by provider</th>
                    <th className="pb-2 pr-3 text-right font-medium">Recorded cost</th>
                    <th className="pb-2 pr-3 text-right font-medium">Gap</th>
                    <th className="pb-2 font-medium">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {view.runs.map((r) => <RunRow key={r.id} run={r} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} />)}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </GlassCard>
  );
}
